'use strict';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {stateGo} from 'redux-ui-router';
import {STATE_CHANGE_ERROR} from 'redux-ui-router/lib/action-types';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/takeUntil';
import {Observable} from 'rxjs/Observable';
import {stateLoadError} from '../../app/actions';
import {RouterStateError} from '../../app/enums';
import {onLocationChangeStart} from '../../config/actions';
import {isCordovaBuild, LANDING_VIEW} from '../../config/constants';
import {REFRESH_TOKEN} from '../../config/constants/auth-keys';
import {epicErrorHandler} from '../../core-utilities/rx-utils';
import {TOAST_TYPES} from '../../forms/enums/ToastTypes';
import {initAuthComplete, requestLogout} from '../actions';
import {getApiToken, getIsAuthenticated} from '../reducers';
import {refreshUserRequest} from '../redux/refreshUser';
import {authStates, getLoginView} from '../router';
import {EXPIRED, EXPIRING, getTokenStatus} from '../utils/token';
import {logger} from './logger';


onTestTokenExpiration.$inject = [
  'authCacheService',
];

onStateChangeError.$inject = [
  '$toast',
];

export const epics = [
  onTestTokenExpiration,
  onStateChangeError,
];

/**
 * @param {ActionsObservable<ReduxAction>} action$
 * @param {Store} store
 * @param dependencies
 * @return {Observable<ReduxAction>}
 */
export function onTestTokenExpiration(action$, store, dependencies) {
  // HACK: This is hacky but will require a bit of refactoring to get this wired more cleanly.
  onExpired.dependencies = dependencies;
  onExpiringSoon.dependencies = dependencies;
  return Observable.combineLatest(
    action$.ofType(initAuthComplete.TYPE).take(1),
    action$.ofType(onLocationChangeStart.TYPE),
    (_, a) => a
  )
    .switchMap(function () {
      const token = getApiToken(store.getState());
      const tokenStatus = getTokenStatus(token);
      let result$ = null;

      if (tokenStatus === EXPIRED) {
        result$ = onExpired();
      } else if (tokenStatus === EXPIRING) {
        result$ = onExpiringSoon();
      }

      return result$ !== null ? result$ : Observable.empty();
    })
    .catch(epicErrorHandler(logger, 'onTestTokenExpiration'));
}

// we should refresh or renew the token prior to expiration if the app is open.
// if the app was not open, the initAuth flow should cover the expired token case.
function onExpiringSoon() {
  // TODO: implement token renewal if expiration is soon (like, <5-10%)
  if (isCordovaBuild()) {
    return Observable.fromPromise(onExpiringSoon.dependencies.authCacheService.getItem(REFRESH_TOKEN))
      .catch(() => Observable.empty())
      .mergeMap(refreshToken => {
        if (refreshToken) {
          return Observable.of(refreshUserRequest.actions.sendRequest({refreshToken}));
        } else {
          return Observable.empty();
        }
      });
  }
}

export function onExpired() {
  if (isCordovaBuild()) {
    return Observable.fromPromise(onExpired.dependencies.authCacheService.getItem(REFRESH_TOKEN))
      .mergeMap(refreshToken => {
        if (refreshToken) {
          return Observable.of(refreshUserRequest.actions.sendRequest({refreshToken}));
        } else {
          return Observable.of(requestLogout());
        }
      })
      .catch(() => Observable.of(requestLogout()));
  } else {
    return Observable.of(requestLogout());
  }
}

/**
 * @param {ActionsObservable<ReduxAction>} action$
 * @param {Store} store
 * @param dependencies
 * @return {Observable<ReduxAction>}
 */
export function onStateChangeError(action$, store, dependencies) {
  return action$.ofType(STATE_CHANGE_ERROR, stateLoadError.TYPE)
    .switchMap((action) => {
      const $toast = dependencies.$toast;
      const {err} = action.payload;

      logger.error({err}, 'onStateChangeError');

      if (isObject(err) && isString(err.code)) {
        switch (err.code) {
          case RouterStateError.NO_DATA:
            $toast.showI18nMessage({
              messageId: isString(err.messageId) ? err.messageId : 'state.no.data.message',
              type: TOAST_TYPES.error,
            });
            return Observable.of(stateGo(isString(err.fallback) ? err.fallback : LANDING_VIEW));

        }
      }

      if (!get(action.payload, 'toState.name')) {
        if (getIsAuthenticated(store.getState())) {
          // this should not recurse, since next time toState.name should be defined
          return Observable.of(stateGo(LANDING_VIEW));
        } else {
          return Observable.of(stateGo(getLoginView()));
        }
      }

      const toStateName = action.payload.toState.name;

      // Handle any special cases here
      if (toStateName === authStates.invitationRespond
        && action.payload.toParams.accessCode) {
        // if we can recover, do so
        return Observable.of(stateGo(authStates.invitationLogin, {accessCode: action.payload.toParams.accessCode}));
      }

      return Observable.of(stateGo(getLoginView()));
    });
}



// WEBPACK FOOTER //
// ./src/js/auth/epics/onLocationChange.js